/**
 * Builds Highcharts theme options from the current app theme colors.
 * This is the single source of truth for chart styling — called at startup
 * (via NCSApp.js) and on theme change (via NCSApp.vue).
 *
 * By owning all visual values in JS rather than relying on styledMode CSS,
 * exported charts (PDF, SVG, PNG, print) carry their own styling and render
 * correctly without needing the live app's CSS context.
 */
import Highcharts from 'highcharts'

import GlobalPrefsService from "./globalPrefsService"

// Safely converts a hex/rgb color to rgba with given opacity.
// Guards against Highcharts.color not being available during early initialization.
function rgbaFromColor(color, opacity) {
    if (Highcharts?.color) {
        return Highcharts.color(color).setOpacity(opacity).get('rgba')
    }

    return color
}

export function getHighchartsThemeOptions() {
    const themeColors = GlobalPrefsService.getAllColors() || {}
    const palette = GlobalPrefsService.getSeriesPalette()

    const bg = themeColors.surface || themeColors.background || '#ffffff'
    const fg = themeColors.text || '#000000'
    const mutedFg = themeColors.softText || fg
    const linkColor = themeColors.link || fg

    const primaryText = rgbaFromColor(fg, 0.8)
    const secondaryText = rgbaFromColor(fg, 0.6)
    const axisLine = primaryText
    const majorGrid = rgbaFromColor(fg, 0.1)
    const minorGrid = rgbaFromColor(fg, 0.05)
    const buttonHoverFill = rgbaFromColor(fg, 0.12)

    return {
        colors: palette,
        chart: {
            backgroundColor: bg,
            loading: {
                style: {
                    padding: '20px',
                    zIndex: 5
                }
            }
        },
        title: {
            style: {
                color: primaryText,
                fontSize: '1em'
            }
        },
        subtitle: {
            style: {
                color: secondaryText
            }
        },
        legend: {
            itemStyle: {
                color: primaryText,
                fontWeight: 'normal',
                textOverflow: undefined
            },
            itemHoverStyle: {
                color: fg,
                fontWeight: 'normal'
            },
            itemHiddenStyle: {
                color: secondaryText
            }
        },
        xAxis: {
            lineColor: axisLine,
            tickColor: axisLine,
            minorTickColor: axisLine,
            gridLineColor: majorGrid,
            minorGridLineColor: minorGrid,
            labels: {
                style: {
                    color: primaryText
                }
            },
            title: {
                style: {
                    color: secondaryText,
                    fontWeight: 'normal'
                }
            }
        },
        yAxis: {
            lineColor: axisLine,
            tickColor: axisLine,
            minorTickColor: axisLine,
            gridLineColor: majorGrid,
            minorGridLineColor: minorGrid,
            labels: {
                style: {
                    color: primaryText
                }
            },
            title: {
                style: {
                    color: secondaryText,
                    fontWeight: 'normal'
                }
            }
        },
        tooltip: {
            backgroundColor: bg,
            borderColor: axisLine,
            style: {color: primaryText}
        },
        drilldown: {
            activeAxisLabelStyle: {
                color: linkColor,
                textDecoration: 'underline'
            },
            activeDataLabelStyle: {
                color: linkColor,
                textDecoration: 'underline'
            },
            breadcrumbs: {
                style: {
                    color: fg
                }
            }
        },
        plotOptions: {
            line: {
                marker: {
                    radius: 4.4
                }
            },
            area: {
                marker: {
                    radius: 4.4
                }
            },
            series: {
                borderWidth: 0,
                borderColor: 'transparent'
            },
            column: {
                borderWidth: 0,
                borderColor: 'transparent'
            },
            bar: {
                borderWidth: 0,
                borderColor: 'transparent'
            },
            pie: {
                borderWidth: 0,
                borderColor: 'transparent'
            }
        },
        exporting: {
            buttons: {
                contextButton: {
                    symbolFill: mutedFg,
                    symbolStroke: mutedFg,
                    theme: {
                        fill: 'transparent',
                        stroke: 'transparent',
                        padding: 0,
                        states: {
                            hover: {
                                fill: buttonHoverFill,
                                stroke: 'transparent'
                            },
                            select: {
                                fill: buttonHoverFill,
                                stroke: 'transparent'
                            }
                        }
                    }
                }
            }
        },
        noData: {
            style: {
                fontWeight: 'normal',
                fontSize: '13px',
                color: rgbaFromColor(fg, 0.87)
            }
        }
    }
}

export function applyHighchartsTheme() {
    Highcharts.setOptions(getHighchartsThemeOptions())
}